<template>
  <v-container
    class="app"
    style="overflow-y:auto;"
    :class="[{ isOversizeOverflowControl: !isOversizeComponent }]"
  >
    <v-container v-show="showDropdown" class="wrap">
      <vue-cascader-select
        :options="options"
        value=""
        placeholder="Select..."
        @clear="val => (value = '')"
        @select="onSelect"
      />
    </v-container>

    <v-container
      v-if="!showDropdown"
      style="padding: 0; margin-top: 0.5rem; margin-bottom: 0.5rem;"
    >
      <v-row class="d-flex justify-center align-center">
        <!-- title -->
        <v-col cols="auto" class="text-center pb-0 pt-2">
          <v-container class="ma-0 pa-0">
            <v-row class="ma-0 pa-0">
              <v-col cols="12" class="ma-0 pa-0">
                <v-card-title class="primary--text pa-0 pl-2 export-content">
                  <v-tooltip
                    v-if="selectedOutcomeVariable"
                    bottom
                    color="primary"
                  >
                    <template v-slot:activator="{ on: tooltip }">
                      <img
                        :src="
                          '/images/' +
                            selectedOutcomeVariable.category +
                            '-icon-128.png'
                        "
                        :title="selectedOutcomeVariable.category"
                        style="height:1.25em"
                        class="ma-1"
                        @error="missingDomainFallback"
                        v-on="{ ...tooltip }"
                      />
                    </template>
                    <span>{{ selectedOutcomeVariable.category }}</span>
                  </v-tooltip>

                  <v-tooltip v-if="selectedOutcomeVariable" top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span class="text-subtitle-3" v-on="{ ...tooltip }">
                        {{
                          selectedOutcomeVariable.abbreviation ===
                          selectedOutcomeVariable.label
                            ? selectedOutcomeVariable.abbreviation
                            : selectedOutcomeVariable.abbreviation +
                              ' | ' +
                              selectedOutcomeVariable.label
                        }}
                      </span>
                    </template>
                    <span class="text-subtitle-3">{{
                      selectedOutcomeVariable.description
                    }}</span>
                  </v-tooltip>

                  <span v-else> {{ title }} </span>
                  <v-spacer />
                </v-card-title>
              </v-col>
            </v-row>
          </v-container>
        </v-col>
        <!-- reset -->
        <v-col cols="auto" class="text-center pb-0 pt-2">
          <v-btn
            dark
            lign-center
            justify-center
            class="secondary mx-2 position-absolute smallest-button"
            x-small
            @click="onReset"
          >
            reset
          </v-btn>
        </v-col>
        <!-- export -->
        <v-col cols="auto" class="text-center pb-0 pt-2">
          <export-interface
            :sec-level-parent="true"
            :multi-select="true"
            :analysis="analysis"
            :variable="selectedOutcomeVariable"
            :export-type="currentComponent"
          />
        </v-col>
      </v-row>
    </v-container>

    <div
      style="padding: 0;"
      class="mt-6 export-content"
      :class="{ isOversizeWidthControl: isOversizeComponent }"
    >
      <!-- ------------------------------------ longitudinalGraph -------------------------------------------- -->
      <!-- analysis = predictor -->
      <!-- detailed-view = single(important) outcome variable -->
      <!-- visible-cohorts = use prop from parent -->
      <detailed-view
        v-if="!showDropdown && currentComponent === 'longitudinalGraph'"
        ref="dview"
        :analysis="analysis"
        :outcome-var="selectedOutcomeVariable"
        :cohorts="visibleCohorts"
        :show-title-bar="false"
        min-height="400px"
      />
      <!-- ------------------------------------ longitudinalGraph -------------------------------------------- -->

      <!-- ------------------------------------ audiogram ------------------------------------------------------- -->
      <audiogram
        v-if="!showDropdown && currentComponent === 'Audiogram'"
        ref="audiogram"
        :analysis="analysis"
        :visible-cohorts="visibleCohorts"
        :show-title-bar="false"
        min-height="400px"
      />
      <!-- ------------------------------------ audiogram ------------------------------------------------------- -->

      <!-- ------------------------------------ boxplot ------------------------------------------------------- -->
      <!-- Ideally in the future some of the parameter should be calculated locally instead of being passed as props -->
      <box-plots
        v-if="
          !showDropdown &&
            currentComponent === 'boxplot' &&
            selectedOutcomeVariable &&
            selectedOutcomeVariable.data_category == 'Continuous'
        "
        ref="boxplots"
        :analysis="analysis"
        :cohorts="visibleCohorts"
        :show-title-bar="false"
        :show-full-panel-view="true"
        :outcome-var="selectedOutcomeVariable"
        :detailed-view="selectedOutcomeVariable"
        :max-cohorts="boxplotMaxCohorts"
        :row-height="boxplotRowHeight"
        :row-pad="boxplotRowPad"
        :bar-pad="boxplotBarPad"
        :title-class="boxplotTitleClass"
        :export-button-size="boxplotExportButtonSize"
        :min-height="50"
        :selected-analysis-mode="selectedAnalysisMode"
        style="margin-top: -30px;"
      />
      <stacked-bars
        v-else-if="!showDropdown && currentComponent === 'boxplot'"
        ref="stackedbars"
        :analysis="analysis"
        :cohorts="visibleCohorts"
        :show-title-bar="false"
        :outcome-var="selectedOutcomeVariable"
        :max-cohorts="analysis.cohorts.length"
        :row-height="boxplotRowHeight"
        :row-pad="boxplotRowPad"
        :bar-pad="boxplotBarPad"
        :title-class="boxplotTitleClass"
        :selected-analysis-mode="selectedAnalysisMode"
        :export-button-size="boxplotExportButtonSize"
      />
      <!-- ------------------------------------ boxplot ------------------------------------------------------- -->

      <!-- ------------------------------ pairwise comparison ------------------------------------------------- -->
      <tukey-hsd-heatmap
        v-if="!showDropdown && currentComponent === 'PairwiseComparison'"
        ref="tukeyhsd"
        :analysis="analysis"
        :cohorts="analysis.cohorts"
        :visible-cohorts="visibleCohorts"
        :outcome-var="selectedOutcomeVariable"
        color-scheme="brewer5"
        :show-title-bar="false"
        :show-full-panel-view="true"
        :selected-analysis-mode="selectedAnalysisMode"
        @selectedCohorts="updateVisibleCohorts"
      />
      <!-- ------------------------------ pairwise comparison ------------------------------------------------- -->

      <!-- ------------------------------ forest plot ------------------------------------------------- -->
      <forest-plot
        v-if="!showDropdown && currentComponent === 'Forest Plot'"
        ref="forestplot"
        :analysis="analysis"
        :visible-cohorts="visibleCohorts"
        :selected-analysis-mode="selectedAnalysisMode"
      />
      <!-- ------------------------------ forest plot ------------------------------------------------- -->

      <!-- ------------------------------ Sample Characteristics ---------------------------------------------- -->
      <summary-stats-table
        v-if="!showDropdown && currentComponent === 'Sample Characteristics'"
        :predictor-variables="analysis.predictorVariables"
        :outcome-variables="analysis.outcomeVariables"
        :selected-variables="selectedOutcomeVariable"
        :cohorts="analysis.cohorts"
        :show-category-icons="true"
        :comparison-field="analysis.input.comparisonField"
        :is-longitudinal="analysis.collection.is_longitudinal"
        :analysis="analysis"
        :hide-export-button="true"
        :selected-analysis-mode="selectedAnalysisMode"
        display-mode="table1"
        @variableSelected="variableSelected"
      />
      <!-- ------------------------------ Sample Characteristics ---------------------------------------------- -->

      <!-- ------------------------------ Summary of Results ------------------------------------------------- -->
      <summary-stats-table
        v-if="!showDropdown && currentComponent === 'Summary of Results'"
        :predictor-variables="analysis.predictorVariables"
        :outcome-variables="analysis.outcomeVariables"
        :selected-variables="selectedOutcomeVariable"
        :cohorts="analysis.cohorts"
        :visible-cohorts="visibleCohorts"
        :show-category-icons="true"
        :comparison-field="analysis.input.comparisonField"
        :is-longitudinal="analysis.collection.is_longitudinal"
        :analysis="analysis"
        :anova-pvals="analysis.pvals"
        display-mode="table2"
        :hide-export-button="true"
        :selected-analysis-mode="selectedAnalysisMode"
        @selectedCohorts="updateVisibleCohorts"
        @variableSelected="variableSelected"
      />
      <!-- ------------------------------ Summary of Results ------------------------------------------------- -->

      <!-- ------------------------------ Outcome Variables ------------------------------------------------- -->
      <analytics-panel
        v-if="
          !showDropdown &&
            (currentComponent === 'Outcome Variables' ||
              currentComponent === 'Predictor Variables')
        "
        ref="apanel"
        :analysis="analysis"
        :selected-variables="selectedOutcomeVariable"
        :anova-pvals="analysis.pvals"
        :anova-pvals-input="analysis.input"
        :outcome-variables="analysis.outcomeVariables"
        :show-title-bar="false"
        :multi-select="false"
        autoselect-first-variable
        :expanded="true"
        color-scheme="brewer5"
        @variablesSelected="variablesSelected"
        @expandClicked="analyticsExpandClicked"
      />
      <!-- ------------------------------ Outcome Variables ------------------------------------------------- -->

      <!-- ------------------------------ Predictors ------------------------------------------------- -->
      <cohort-table
        v-if="
          !showDropdown &&
            (currentComponent === 'Predictors' ||
              currentComponent === 'Outcomes')
        "
        :cohorts="analysis.cohorts"
        :select-cohorts="visibleCohorts"
        :show-title-bar="false"
        :expanded="true"
        report-max-overlap
        show-select
        show-colors
        @selectedCohorts="updateVisibleCohorts"
        @cohortColorChange="updateCohortColor"
      />
      <!-- ------------------------------ Predictors ------------------------------------------------- -->
    </div>
  </v-container>
</template>

<script>
import VueCascaderSelect from 'vue-cascader-select';
import SummaryStatsTable from '@/components/AnalysisSummary/SummaryStatsTable.vue';
import CohortTable from '@/components/common/CohortTable.vue';
import AnalyticsPanel from '@/components/DataExplorer/AnalyticsPanel.vue';
import DetailedView from '@/components/DataExplorer/DetailedView.vue';
import Audiogram from '@/components/DataExplorer/Audiogram.vue';
import BoxPlots from '@/components/AnalysisSummary/BoxPlots.vue';
import StackedBars from '@/components/AnalysisSummary/StackedBars.vue';
import TukeyHsdHeatmap from '@/components/AnalysisSummary/TukeyHSDHeatmap.vue';
import ForestPlot from '@/components/AnalysisSummary/ForestPlot.vue';
import ExportInterface from '@/components/common/ExportInterface.vue';
import {
  getCollectionHasPureToneAudiometryData,
  missingDomainFallback,
} from '@/utils/helpers';

export default {
  components: {
    VueCascaderSelect,
    DetailedView,
    SummaryStatsTable,
    TukeyHsdHeatmap,
    AnalyticsPanel,
    Audiogram,
    BoxPlots,
    StackedBars,
    CohortTable,
    ExportInterface,
    ForestPlot,
  },
  props: {
    // longitudinalGraph
    // -----------------------------
    analysis: {
      type: Object,
      required: true,
      default: () => {},
    },
    visibleCohorts: {
      type: Array,
      required: true,
      default: () => [],
    },

    // boxplot
    // -----------------------------
    boxplotMaxCohorts: {
      type: Number,
      required: false,
      default: 4,
    },
    boxplotRowHeight: {
      type: Number,
      required: false,
      default: 100,
    },
    boxplotRowPad: {
      type: Number,
      required: false,
      default: 12,
    },
    boxplotBarPad: {
      type: Number,
      required: false,
      default: 5,
    },
    boxplotTitleClass: {
      type: String,
      required: false,
      default: '',
    },
    boxplotExportButtonSize: {
      type: String,
      required: false,
      default: 'small',
    },
    // pairwise comparison
    // -----------------------------
    initialSelection: {
      type: Object,
      required: false,
      default: null,
    },
    selectedAnalysisMode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      options: [],
      showDropdown: true,
      currentComponent: null,
      selectedOutcomeVariable: null,
      isOversizeComponent: false,
      title: '',
      missingDomainFallback: missingDomainFallback,
    };
  },
  watch: {
    //watch for change in outcomeVariables and change title accordingly
    currentComponent(newVal) {
      if (
        newVal === 'longitudinalGraph' ||
        newVal === 'boxplot' ||
        newVal === 'PairwiseComparison'
      ) {
        this.title =
          this.selectedOutcomeVariable.abbreviation ===
          this.selectedOutcomeVariable.label
            ? this.selectedOutcomeVariable.abbreviation
            : this.selectedOutcomeVariable.abbreviation +
              ' | ' +
              this.selectedOutcomeVariable.label;
      } else {
        if (
          newVal == 'Predictors' &&
          this.selectedAnalysisMode == 'Predictor based'
        ) {
          this.title = 'Study groups';
        } else if (
          newVal == 'Outcomes' &&
          this.selectedAnalysisMode != 'Predictor based'
        ) {
          this.title = 'Study groups';
        } else {
          this.title = newVal;
        }
      }
    },
  },
  mounted() {
    this.createOption();
    if (this.initialSelection) {
      this.onSelect(this.initialSelection);
    }
  },
  methods: {
    // -------------------------------------------- Multi-Level Dropwdown --------------------------------------------
    // Initializes the options for the dropdown
    // Based on Vuex Binding outcomeVariables
    createOption() {
      this.options = [];
      // audiogram
      if (getCollectionHasPureToneAudiometryData(this.analysis.collection)) {
        this.options.push({
          label: 'Audiogram',
          value: 'Audiogram',
          disabled: true,
          options: [{ label: 'Audiogram', value: 'Audiogram' }],
        });
      }

      // boxplot
      this.options.push({
        label: 'Boxplot/Bar graph',
        value: 'Boxplot/Bar graph',
        disabled: true,
        options: this.analysis.outcomeVariables.map(variable => {
          return {
            label: variable.abbreviation,
            value: 'boxplot->' + variable.abbreviation,
          };
        }),
      });

      //longitudinal
      if (this.analysis.collection.is_longitudinal) {
        this.options.push({
          label: 'Longitudinal',
          value: 'Longitudinal',
          disabled: true,
          options: this.analysis.outcomeVariables.map(variable => {
            return {
              label: variable.abbreviation,
              value: 'longitudinalGraph->' + variable.abbreviation,
            };
          }),
        });
      }
      //Pairwise Comparisons
      this.options.push({
        label: 'Pairwise Comparison',
        value: 'Pairwise Comparison',
        disabled: true,
        options: this.analysis.outcomeVariables.map(variable => {
          return {
            label: variable.abbreviation,
            value: 'PairwiseComparison->' + variable.abbreviation,
          };
        }),
      });

      if (this.selectedAnalysisMode == 'Outcome based') {
        this.options.push({
          label: 'Regression Analysis',
          value: 'Regression Analysis',
          disabled: true,
          options: [{ label: 'Forest Plot', value: 'Forest Plot' }],
        });
      }

      // Sample characteristics
      this.options.push({
        label: 'Sample Characteristics',
        value: 'Sample Characteristics',
      });

      // Summary of Results
      this.options.push({
        label: 'Summary of Results',
        value: 'Summary of Results',
      });

      if (this.selectedAnalysisMode == 'Predictor based') {
        // Outcome Variables
        this.options.push({
          label: 'Outcome Variables',
          value: 'Outcome Variables',
        });

        // Predictors
        this.options.push({
          label: 'Study groups',
          value: 'Predictors',
        });
      } else {
        this.options.push({
          label: 'Predictor Variables',
          value: 'Predictor Variables',
        });

        this.options.push({
          label: 'Study groups',
          value: 'Outcomes',
        });
      }
    },
    // this function shouldn't be needed but I am keeping it here for now
    testDelete() {
      var div = document.getElementById('div');
      var range = document.createRange();
      var content = div.firstChild;
      range.setStart(content, 1);
      range.setEnd(content, 7);
      range.deleteContents();
    },

    // -------------------------------------------- Rendering Component --------------------------------------------
    // this function is called when a user selects an option
    // It will then render the coreponding component
    onSelect(selected) {
      this.showDropdown = false;
      this.value = selected.value; // this.value will look like "longitudinalGraph->Benton JOLO" or "boxplot->Benton JOLO"
      let label = selected.label; // this.label will look like "Benton JOLO"
      // select object from outcomeVariables array that has the same abbreviation as label
      this.selectedOutcomeVariable = this.analysis.outcomeVariables.find(
        variable => variable.abbreviation === label
      );

      //if this.value contains "longitudinalGraph" then render the longitudinalGraph component
      if (this.value.includes('longitudinalGraph')) {
        //console.log("rendering longitudinalGraph");
        this.currentComponent = 'longitudinalGraph';
      } else if (this.value.includes('Audiogram')) {
        //console.log("rendering boxplot");
        this.currentComponent = 'Audiogram';
      }
      //if this.value contains "boxplot" then render the boxplot component
      else if (this.value.includes('boxplot')) {
        //console.log("rendering boxplot");
        this.currentComponent = 'boxplot';
      }
      //if this.value contains "PairwiseComparison" then render the PairwiseComparison component
      else if (this.value.includes('PairwiseComparison')) {
        //console.log("rendering PairwiseComparison");
        this.currentComponent = 'PairwiseComparison';
        this.isOversizeComponent = true;
      }
      //if this.value contains "Sample Characteristics" then render the Sample Characteristics component
      else if (this.value.includes('Sample Characteristics')) {
        //console.log("rendering Sample Characteristics");
        this.currentComponent = 'Sample Characteristics';
      }
      //if this.value contains "Summary of Results" then render the Summary of Results component
      else if (this.value.includes('Summary of Results')) {
        //console.log("rendering Summary of Results");
        this.currentComponent = 'Summary of Results';
        this.isOversizeComponent = true;
      }
      //if this.value contains "Outcome Variables" then render the Outcome Variables component
      else if (this.value.includes('Outcome Variables')) {
        //console.log("rendering Outcome Variables");
        this.currentComponent = 'Outcome Variables';
      } else if (this.value.includes('Predictor Variables')) {
        //console.log("rendering Outcome Variables");
        this.currentComponent = 'Predictor Variables';
      }
      //if this.value contains "Predictors" then render the Predictors component
      else if (this.value.includes('Predictors')) {
        //console.log("rendering Predictors");
        this.currentComponent = 'Predictors';
      } else if (this.value.includes('Outcomes')) {
        //console.log("rendering Predictors");
        this.currentComponent = 'Outcomes';
      } else if (this.value.includes('Forest Plot')) {
        //console.log("rendering Predictors");
        this.currentComponent = 'Forest Plot';
      }
    },

    // -------------------------------------------- Reset --------------------------------------------
    onReset() {
      this.isOversizeComponent = false;
      this.showDropdown = true;
    },
    // -------------------------------------------- Children Emit Function --------------------------------------------
    // Note that this component is the middleman, it doesn't need to know the detail of the functions
    // These functions will be passed up to this component's parent: AnalysisMultiPanel

    updateVisibleCohorts(nsel) {
      this.$emit('selectedCohorts', nsel);
    },
    variablesSelected(ovs) {
      this.$emit('variablesSelected', ovs);
    },
    variableSelected(ov) {
      this.$emit('variableSelected', ov);
    },
    analyticsExpandClicked(expand) {
      this.$emit('expandClicked', expand);
    },
    updateCohortColor(cce) {
      this.$emit('cohortColorChange', cce);
    },

    // -------------------------------------------- Other --------------------------------------------
  },
};
</script>

<style lang="scss">
.app {
  height: 100%;
  padding: 0px;
}
.wrap {
  width: 300px;
}
::v-deep .vcs__select-menu {
  position: relative;
  color: black;
}

.isOversizeWidthControl {
  width: fit-content;
}

.isOversizeOverflowControl {
  overflow-x: hidden;
}
</style>
